import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Mail, Save, Loader2, Info } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Sender details for the auto-email engine. Lives in Settings → Preferences.
// Super-admin (`global` scope) and each sub-broker (`sb_<code>` scope) get
// their own doc, so every associate can run automated reporting for the
// clients they own. Test send stays inside Portfolio → Reporting Preferences
// so the same scope-aware doc is exercised end-to-end.
const ReportingDeliveryCard = () => {
  const { user, api } = useAuth();
  const role = user?.role;
  const isSuperAdmin = role === "super_admin" || role === "broker" || role === "platform_owner";
  const isAssociate = role === "sub_broker";
  const canManage = isSuperAdmin || isAssociate;

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      try {
        const r = await api.get("/reporting-preferences");
        setPrefs(r.data);
      } catch (_e) {
        toast.error("Failed to load delivery settings");
      } finally {
        setLoading(false);
      }
    })();
  }, [api, canManage]);

  if (!canManage) return null;
  if (loading || !prefs) {
    return (
      <Card className="border-gray-200">
        <CardContent className="py-8 flex items-center justify-center text-gray-500 gap-2 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading delivery settings…
        </CardContent>
      </Card>
    );
  }

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put("/reporting-preferences", prefs);
      setPrefs(r.data);
      toast.success("Sender details saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const scopeLabel = isSuperAdmin
    ? "firm-wide (all clients without an assigned associate)"
    : `your mapped clients (associate code ${user?.sub_broker_code || "—"})`;

  return (
    <div className="space-y-4" data-testid="reporting-delivery-section">
      {/* Instructions — different copy per role */}
      <div className="flex gap-3 p-4 rounded-lg border border-teal-100 bg-teal-50/40">
        <Info className="w-4 h-4 text-teal-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-700 space-y-2">
          <p className="font-medium text-gray-900">
            How automated client reporting works
          </p>
          {isSuperAdmin ? (
            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
              <li>This is the <b>firm-wide fallback sender</b> — used for every client who isn't mapped to an associate.</li>
              <li>Each associate also has their own sender block here under <b>their</b> Settings → Preferences. Their config is used for the clients they manage.</li>
              <li>Configure From-email / Display name / Reply-to and pick the delivery provider. Provider creds (Gsuite SMTP password, Resend API key, etc.) live in the backend <code>.env</code>.</li>
              <li>Schedule, report toggles and per-client opt-outs live under <b>Portfolio → Reporting Preferences</b>. Test send is on the same tab.</li>
            </ul>
          ) : (
            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
              <li>You're configuring the sender used for the auto-emails to <b>{scopeLabel}</b>. Each associate manages their own block independently.</li>
              <li>Pick a From-email you control (e.g. your own Gsuite account or a kinntegra.co.in alias) — the platform admin needs to register the corresponding credentials in the backend before live sends turn on.</li>
              <li>Schedule (Monthly / Quarterly / etc.), which reports to send, and per-client opt-outs live under <b>Portfolio → Reporting Preferences</b>. Run a test send from there once you've saved sender details here.</li>
              <li>Until provider creds are wired in, the test-send button just confirms the form is valid — it doesn't actually email yet.</li>
            </ul>
          )}
        </div>
      </div>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Mail className="w-4 h-4 text-teal-700" />
            Reporting · Sender details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-gray-600">From email</Label>
            <Input
              type="email"
              placeholder="donotreply@kinntegra.co.in"
              value={prefs.sender_email}
              onChange={(e) => set("sender_email", e.target.value)}
              className="mt-1"
              data-testid="settings-sender-email"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Display name</Label>
            <Input
              placeholder="Kinntegra"
              value={prefs.sender_name}
              onChange={(e) => set("sender_name", e.target.value)}
              className="mt-1"
              data-testid="settings-sender-name"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Reply-to (optional)</Label>
            <Input
              type="email"
              placeholder="reports@kinntegra.co.in"
              value={prefs.reply_to}
              onChange={(e) => set("reply_to", e.target.value)}
              className="mt-1"
              data-testid="settings-reply-to"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs text-gray-600">Delivery provider</Label>
            <Select
              value={prefs.delivery_provider}
              onValueChange={(v) => set("delivery_provider", v)}
            >
              <SelectTrigger className="mt-1 max-w-xs" data-testid="settings-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Not configured yet</SelectItem>
                <SelectItem value="GSUITE">Google Workspace (Gsuite SMTP)</SelectItem>
                <SelectItem value="PRIVATE_SMTP">Private SMTP relay</SelectItem>
                <SelectItem value="RESEND">Resend</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Provider credentials are configured by the platform admin in the backend environment — this dropdown only records intent.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-teal-700 hover:bg-teal-800 text-white"
          data-testid="settings-delivery-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save sender details
        </Button>
      </div>
    </div>
  );
};

export default ReportingDeliveryCard;
